import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

let pdpMediaLoadingCounter = 0;
const PDP_MEDIA_MIN_LOADING_TIME = 450;
const PDP_MEDIA_MAX_LOADING_TIME = 3000;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (document.documentElement.dataset.pdpMediaLoading) {
      this.#applyLoadingState({ waitForImages: true });
    }
  }

  #controller = new AbortController();
  #loadingToken = '';

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) {
      this.#clearLoadingState();
      return;
    }

    const newMediaGallery = this.#getReplacementGallery(source);

    if (!newMediaGallery) {
      this.#clearLoadingState();
      return;
    }

    // Keep loading state active - new gallery will detect it in connectedCallback
    this.replaceWith(newMediaGallery);
  };

  #applyLoadingState({ waitForImages = false } = {}) {
    if (!this.classList.contains('media-gallery--pdp-hero')) return;

    this.classList.add('media-gallery--loading');
    this.#loadingToken = document.documentElement.dataset.pdpMediaLoading || '';
    this.#applyReservedHeight();

    if (!waitForImages) return;

    const primaryImages = this.#getPrimaryMediaImages();

    if (primaryImages.length === 0) {
      this.#clearLoadingState(this.#loadingToken);
      return;
    }

    let loadedCount = 0;
    const totalImages = primaryImages.length;

    const timeoutId = setTimeout(() => {
      this.#clearLoadingState(this.#loadingToken);
    }, PDP_MEDIA_MAX_LOADING_TIME);

    const finish = () => {
      loadedCount += 1;
      if (loadedCount >= totalImages) {
        clearTimeout(timeoutId);
        this.#clearLoadingState(this.#loadingToken);
      }
    };

    for (const image of primaryImages) {
      if (image.complete && image.naturalWidth > 0) {
        this.#decodeImage(image).finally(finish);
      } else {
        image.addEventListener('load', () => this.#decodeImage(image).finally(finish), { once: true });
        image.addEventListener('error', finish, { once: true });
      }
    }
  }

  #decodeImage(image) {
    if (!image.decode) return Promise.resolve();

    return image.decode().catch(() => undefined);
  }

  #clearLoadingState(token = this.#loadingToken) {
    const isCurrentLoadingState = !token || document.documentElement.dataset.pdpMediaLoading === token;

    if (!isCurrentLoadingState) {
      this.classList.remove('media-gallery--loading');
      return;
    }

    const loadingStartedAt = Number(document.documentElement.dataset.pdpMediaLoadingStarted || 0);
    const loadingElapsed = loadingStartedAt ? performance.now() - loadingStartedAt : PDP_MEDIA_MIN_LOADING_TIME;
    const remainingLoadingTime = Math.max(PDP_MEDIA_MIN_LOADING_TIME - loadingElapsed, 0);

    if (remainingLoadingTime > 0) {
      setTimeout(() => this.#clearLoadingState(token), remainingLoadingTime);
      return;
    }

    this.classList.remove('media-gallery--loading');
    this.style.removeProperty('min-height');

    if (this.#isVisibleGallery() && document.documentElement.dataset.pdpMediaLoading) {
      delete document.documentElement.dataset.pdpMediaLoading;
      delete document.documentElement.dataset.pdpMediaLoadingStarted;
      delete document.documentElement.dataset.pdpMediaReservedHeight;
    }
  }

  #applyReservedHeight() {
    const reservedHeight = Number(document.documentElement.dataset.pdpMediaReservedHeight || 0);
    if (!reservedHeight || !Number.isFinite(reservedHeight)) return;

    this.style.minHeight = `${reservedHeight}px`;
  }

  #getPrimaryMediaImages() {
    const images = Array.from(
      this.querySelectorAll(
        ':scope > slideshow-component slideshow-slide:first-of-type .product-media__image, :scope > slideshow-component slideshow-slide:first-of-type .deferred-media__poster-image, :scope > .product-media-container:first-of-type .product-media__image, :scope > .product-media-container:first-of-type .deferred-media__poster-image'
      )
    );

    if (images.length > 0) return images;

    const fallbackImage = this.querySelector(
      ':scope > slideshow-component .product-media__image, :scope > slideshow-component .deferred-media__poster-image, :scope > .product-media-container .product-media__image, :scope > .product-media-container .deferred-media__poster-image'
    );

    return fallbackImage ? [fallbackImage] : [];
  }

  #isVisibleGallery() {
    const styles = getComputedStyle(this);

    return styles.display !== 'none' && styles.visibility !== 'hidden' && this.getClientRects().length > 0;
  }

  #getReplacementGallery(source) {
    if (this.classList.contains('desktop:hidden')) {
      return source.querySelector('media-gallery.desktop\\:hidden');
    }

    if (this.classList.contains('mobile:hidden')) {
      return source.querySelector('media-gallery.mobile\\:hidden');
    }

    return source.querySelector('media-gallery');
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} _event - The variant selected event.
   */
  #handleVariantSelected = (_event) => {
    pdpMediaLoadingCounter += 1;
    document.documentElement.dataset.pdpMediaLoading = String(pdpMediaLoadingCounter);
    document.documentElement.dataset.pdpMediaLoadingStarted = String(performance.now());
    this.#storeReservedHeight();
    this.#applyLoadingState();
  };

  #storeReservedHeight() {
    if (!this.classList.contains('media-gallery--pdp-hero') || !this.#isVisibleGallery()) return;

    const height = this.getBoundingClientRect().height;
    if (height > 0) {
      document.documentElement.dataset.pdpMediaReservedHeight = String(Math.ceil(height));
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
